// Le site du club (D-140, précise D-129), lot A : la logique pure, testée (site.test.ts).
//   * Le document d'un site : son thème, ses pages (adresse, titre, menu, référencement) et leurs blocs (type, variante,
//     options, contenu), en JSON. La base le valide avec la même règle (_site_doc_check ; cas partagés :
//     scripts/bench/site-doc-cases.json).
//   * Des blocs encadrés, pas de mise en page libre : 11 blocs vivants nourris par l'app (prochain match, matchs à
//     domicile, en déplacement, derniers résultats, équipes et créneaux, « Venez essayer », partenaires, agenda,
//     actualités, boutique et paiement, ☆ Suivre ; pas de classement en V1) et 10 blocs fixes.
//   * Les pages d'équipe sont générées (une par groupe), les mentions légales aussi (verrouillées).
//   * L'option « Site personnalisable » : son prix (D-142 : 149 € TTC par an), écrit une seule fois ici et lu partout
//     (offre, assistant, éditeur, Site du club, aide, visites, console) ; la première saison au prorata (D-140), à enlever
//     d'une ligne (PRORATA).
import { slugify } from './slug';

export type SiteTheme = 'tribune' | 'parquet' | 'gazette';
export const SITE_THEMES: { key: SiteTheme; name: string; desc: string }[] = [
  { key: 'tribune', name: 'Tribune', desc: 'Sombre et sportif : grand bandeau, gros titres, matchs en vedette' },
  { key: 'parquet', name: 'Parquet', desc: 'Le plus proche de ta page actuelle : bandeau aux couleurs du club' },
  { key: 'gazette', name: 'Gazette', desc: 'Clair et posé : le journal du club, beaucoup de blanc' },
];
export const themeName = (t: SiteTheme) => SITE_THEMES.find(x => x.key === t)?.name ?? t;

export type LiveType = 'next' | 'homes' | 'away' | 'results' | 'teams' | 'trial' | 'partners' | 'agenda' | 'news' | 'shop' | 'follow';
export type FixedType = 'text' | 'image' | 'gallery' | 'video' | 'button' | 'figures' | 'faq' | 'map' | 'contact' | 'docs';
export type BlockType = LiveType | FixedType;
export const LIVE_TYPES: LiveType[] = ['next', 'homes', 'away', 'results', 'teams', 'trial', 'partners', 'agenda', 'news', 'shop', 'follow'];
export const FIXED_TYPES: FixedType[] = ['text', 'image', 'gallery', 'video', 'button', 'figures', 'faq', 'map', 'contact', 'docs'];
export const isLive = (t: BlockType): t is LiveType => (LIVE_TYPES as string[]).includes(t);

export interface BlockDef {
  type: BlockType;
  label: string;
  /** Icône du panneau des blocs (clé de src/ui/site-icons.ts). */
  icon: string;
  variants: { key: string; label: string }[];
  /** Mots pour « Chercher un bloc ». */
  words: string;
}
export const BLOCKS: Record<BlockType, BlockDef> = {
  next: { type: 'next', label: 'Prochain match', icon: 'ball', variants: [{ key: 'bandeau', label: 'Bandeau' }], words: 'match prochain vedette affiche' },
  homes: { type: 'homes', label: 'Matchs à domicile', icon: 'home', variants: [{ key: 'cartes', label: 'Cartes' }, { key: 'liste', label: 'Liste' }, { key: 'bandeau', label: 'Bandeau' }], words: 'domicile matchs calendrier encourager entrée' },
  away: { type: 'away', label: 'En déplacement', icon: 'plane', variants: [{ key: 'liste', label: 'Liste' }], words: 'extérieur déplacement route matchs' },
  results: { type: 'results', label: 'Derniers résultats', icon: 'trophy', variants: [{ key: 'cartes', label: 'Cartes' }, { key: 'liste', label: 'Liste' }], words: 'résultats scores victoires week-end' },
  teams: { type: 'teams', label: 'Équipes et créneaux', icon: 'people', variants: [{ key: 'cartes', label: 'Cartes' }], words: 'équipes groupes entraînements créneaux horaires' },
  trial: { type: 'trial', label: 'Venez essayer', icon: 'door', variants: [{ key: 'image', label: 'Avec image' }, { key: 'centre', label: 'Centré' }], words: 'essai recruter inscription rejoindre' },
  partners: { type: 'partners', label: 'Partenaires', icon: 'hand', variants: [{ key: 'grille', label: 'Grille' }, { key: 'bandeau', label: 'Bandeau' }], words: 'partenaires sponsors soutien don' },
  agenda: { type: 'agenda', label: 'Agenda', icon: 'cal', variants: [{ key: 'cartes', label: 'Cartes' }], words: 'agenda événements tournoi loto rendez-vous' },
  news: { type: 'news', label: 'Actualités', icon: 'news', variants: [{ key: 'cartes', label: 'Cartes' }, { key: 'liste', label: 'Liste' }], words: 'actualités nouvelles articles blog' },
  shop: { type: 'shop', label: 'Boutique et paiement', icon: 'shop', variants: [{ key: 'cartes', label: 'Cartes' }], words: 'boutique paiement cotisation don helloasso' },
  follow: { type: 'follow', label: '☆ Suivre', icon: 'star', variants: [{ key: 'bandeau', label: 'Bandeau' }], words: 'suivre abonné notifications résultats' },
  text: { type: 'text', label: 'Titre et texte', icon: 'text', variants: [{ key: 'banniere', label: 'Bannière' }, { key: 'image', label: 'Texte et image' }, { key: 'centre', label: 'Centré' }], words: 'titre texte paragraphe bannière accueil' },
  image: { type: 'image', label: 'Image', icon: 'image', variants: [{ key: 'large', label: 'Pleine largeur' }, { key: 'cadre', label: 'Encadrée' }], words: 'image photo' },
  gallery: { type: 'gallery', label: 'Galerie', icon: 'gallery', variants: [{ key: 'grille', label: 'Grille' }, { key: 'bandeau', label: 'Bandeau' }], words: 'galerie photos album' },
  video: { type: 'video', label: 'Vidéo YouTube', icon: 'video', variants: [{ key: 'large', label: 'Grande' }, { key: 'texte', label: 'Avec texte' }], words: 'vidéo youtube film' },
  button: { type: 'button', label: 'Bouton', icon: 'button', variants: [{ key: 'plein', label: 'Plein' }, { key: 'contour', label: 'Contour' }], words: 'bouton lien appel' },
  figures: { type: 'figures', label: 'Chiffres clés', icon: 'figures', variants: [{ key: 'club', label: 'Couleur du club' }, { key: 'clair', label: 'Clair' }], words: 'chiffres nombres statistiques' },
  faq: { type: 'faq', label: 'Questions', icon: 'faq', variants: [{ key: 'liste', label: 'Liste' }], words: 'questions faq réponses' },
  map: { type: 'map', label: 'Plan d’accès', icon: 'map', variants: [{ key: 'large', label: 'Grand' }], words: 'plan accès adresse carte gymnase' },
  contact: { type: 'contact', label: 'Contact', icon: 'mail', variants: [{ key: 'plan', label: 'Avec le plan' }, { key: 'simple', label: 'Simple' }], words: 'contact e-mail bureau salle' },
  docs: { type: 'docs', label: 'Documents', icon: 'doc', variants: [{ key: 'liste', label: 'Liste' }], words: 'documents pdf règlement fiche' },
};

/** Les fonds d'un bloc : image (Titre et texte), couleur du club, sombre, clair, blanc. */
export type Bg = 'image' | 'club' | 'sombre' | 'clair' | 'blanc';
export type Spacing = 's' | 'm' | 'l';

export interface SiteBlock {
  id: string;
  type: BlockType;
  variant: string;
  /** Options : fond, espacement, groupes affichés, nombre, interrupteurs… */
  o: Record<string, unknown>;
  /** Contenu : titres, textes, images (identifiant d'une photo du club), boutons, listes. */
  c: Record<string, unknown>;
}
export type PageKind = 'home' | 'club' | 'teams' | 'matches' | 'partners' | 'shop' | 'contact' | 'legal' | 'custom';
export interface SitePage {
  id: string;
  kind: PageKind;
  /** L'adresse de la page sous le site (« le-club ») ; '' pour l'accueil. */
  slug: string;
  title: string;
  /** Dans le menu du site. */
  menu: boolean;
  /** Titre et description pour Google ; vides : proposés par Arceau. */
  seo: { title?: string | null; desc?: string | null };
  blocks: SiteBlock[];
}
export interface SiteDoc {
  v: 1;
  theme: SiteTheme;
  pages: SitePage[];
  /** Les pages d'équipe générées (une par groupe) : leur ordre, leur référencement, celles qu'on cache. */
  teams: { order: string[]; hidden: string[]; seo: Record<string, { title?: string | null; desc?: string | null }> };
}

/** Les adresses réservées sous le site : elles mènent déjà ailleurs (espace membres, pages sans compte du club). */
export const RESERVED_PAGE_SLUGS = ['app', 'c', 'p', 's', 'i', 'd', 'u', 'f', 'r', 'partenaires', 'donnees', 'assets', 'icons', 'actualites', 'creer', 'site', 'api'];
export const PAGE_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const MAX_PAGES = 40;
export const MAX_BLOCKS = 40;
export const MAX_DOC = 500_000;
export const QUOTA = { photos: 300, pdfs: 50, pdfBytes: 10 * 1024 * 1024, photoPx: 2000 } as const;

/** Les pages proposées par l'assistant « Créer mon site », dans l'ordre du menu. */
export const PREPARED: { kind: Exclude<PageKind, 'custom'>; title: string; slug: string; sub: string; locked?: boolean }[] = [
  { kind: 'home', title: 'Accueil', slug: '', sub: 'Prochain match, matchs à domicile, résultats, « Venez essayer », actualités, partenaires' },
  { kind: 'club', title: 'Le club', slug: 'le-club', sub: 'Le bureau, les salles ; l’histoire du club, à écrire' },
  { kind: 'teams', title: 'Équipes', slug: 'equipes', sub: 'Une par groupe : créneaux, matchs, résultats, « ☆ Suivre »' },
  { kind: 'matches', title: 'Matchs', slug: 'matchs', sub: 'Le calendrier et les résultats de tout le club' },
  { kind: 'partners', title: 'Partenaires', slug: 'nos-partenaires', sub: 'Les partenaires du club, « Devenir partenaire »' },
  { kind: 'shop', title: 'Boutique', slug: 'boutique', sub: 'Cotisation, don au club et tes liens de paiement' },
  { kind: 'contact', title: 'Contact', slug: 'contact', sub: 'Salles, e-mail, plan d’accès, le bureau' },
  { kind: 'legal', title: 'Mentions légales', slug: 'mentions-legales', sub: 'Générées : l’association éditrice, IDR hébergeur. Obligatoires.', locked: true },
];

let seq = 0;
/** Un identifiant de bloc ou de page (lettres et chiffres, pas d'information). */
export function newId(prefix = 'b'): string {
  seq = (seq + 1) % 1296;
  return `${prefix}${Date.now().toString(36).slice(-5)}${Math.random().toString(36).slice(2, 6)}${seq.toString(36)}`.slice(0, 16);
}

/** Les titres des blocs vivants, proposés (modifiables sur la page). */
export const LIVE_HEADINGS: Partial<Record<BlockType, { k: string; t: string; b?: string }>> = {
  homes: { k: 'Matchs à domicile', t: 'Venez nous encourager' },
  away: { k: 'En déplacement', t: 'Sur la route' },
  results: { k: 'Derniers résultats', t: 'Le week-end dernier' },
  teams: { k: 'Équipes et entraînements', t: 'Trouvez votre groupe' },
  trial: { k: 'Nous rejoindre', t: 'Venez essayer', b: 'Une séance d’essai, de l’école de basket aux seniors. Laissez vos coordonnées : le club vous propose un créneau.' },
  partners: { k: 'Partenaires', t: 'Ils nous soutiennent' },
  agenda: { k: 'Agenda du club', t: 'Les prochains rendez-vous' },
  news: { k: 'Actualités', t: 'La vie du club' },
  shop: { k: 'Boutique', t: 'Soutenir le club', b: 'Cotisation, dons, inscriptions aux événements : le paiement se fait sur le site de l’organisme, le club reçoit tout.' },
  follow: { k: 'Ne ratez plus un match', t: '☆ Suivez votre groupe', b: 'Résultats et changements de dernière minute, sur votre téléphone ou par e-mail. Un compte supporter en deux minutes, sans rien installer.' },
};

/** Un bloc neuf, prêt à poser (le panneau des blocs, l'assistant). */
export function newBlock(type: BlockType, seed: Partial<SiteBlock> = {}): SiteBlock {
  const def = BLOCKS[type];
  const head = LIVE_HEADINGS[type];
  const base: SiteBlock = { id: newId('b'), type, variant: def.variants[0]!.key, o: {}, c: head ? { ...head } : {} };
  switch (type) {
    case 'homes': base.o = { groups: [], n: 4, entry: true, filter: true, follow: false }; break;
    case 'away': base.o = { groups: [], n: 6 }; break;
    case 'results': base.o = { groups: [], n: 6 }; break;
    case 'teams': base.o = { groups: [] }; break;
    case 'next': base.o = { follow: true, agenda: true }; break;
    case 'partners': base.o = { donate: true, become: true }; break;
    case 'agenda': base.o = { n: 4 }; break;
    case 'news': base.o = { n: 3, photos: true }; break;
    case 'text': base.variant = 'centre'; base.o = { bg: 'blanc', sp: 'm', h: 'm' }; base.c = { k: '', t: 'Un titre', b: 'Votre texte ici.', btns: [] }; break;
    case 'image': base.c = { img: null, cap: '' }; break;
    case 'gallery': base.c = { k: 'En images', t: 'La galerie', imgs: [] }; break;
    case 'video': base.c = { k: 'Vidéo', t: 'En vidéo', yt: '', cap: '' }; break;
    case 'button': base.c = { l: 'Venez essayer', to: 'trial' }; break;
    case 'figures': base.c = { items: [{ n: '1', t: 'à vous de compléter' }] }; break;
    case 'faq': base.c = { k: 'Questions', t: 'Vos questions', items: [{ q: 'À partir de quel âge ?', a: 'Dès 5 ans, à l’école de basket.' }] }; break;
    case 'map': base.c = { k: 'Plan d’accès', t: 'Venir au gymnase', addr: '' }; break;
    case 'contact': base.c = { k: 'Contact et accès', t: 'Venir au club' }; base.o = { bureau: true, slots: true }; break;
    case 'docs': base.c = { k: 'Documents', t: 'À télécharger', items: [] }; break;
    default: break;
  }
  return { ...base, ...seed, o: { ...base.o, ...(seed.o ?? {}) }, c: { ...base.c, ...(seed.c ?? {}) } };
}

// --- Ce que l'app sait du club, pour préparer le site --------------------------------------------------------------
export interface SiteFacts {
  name: string;
  tagline: string | null;
  address: string | null;
  groups: { id: string; name: string; category: string | null }[];
  locations: string[];
  partners: number;
  agenda: number;
  volunteerGroups: number;
  donation: boolean;
}

/** L'accroche du bandeau d'accueil : « De l'école de basket aux seniors : 6 groupes, 3 salles et des bénévoles… ». */
export function leadText(f: Pick<SiteFacts, 'groups' | 'locations' | 'address'>): string {
  const g = f.groups.length, s = f.locations.length;
  const parts = [`${g} ${g > 1 ? 'groupes' : 'groupe'}`, s ? `${s} ${s > 1 ? 'salles' : 'salle'}` : ''].filter(Boolean).join(', ');
  return `De l'école de basket aux seniors : ${parts} et des bénévoles qui font tourner le club.${f.address ? ` Les matchs à domicile se jouent au ${f.address}.` : ''}`;
}
/** Une année dans l'accroche (« depuis 1962 ») : le premier chiffre clé du club. */
export const foundingYear = (tagline: string | null | undefined) => /\b(1[89]\d{2}|20[0-2]\d)\b/.exec(tagline ?? '')?.[1] ?? null;

/** Le document préparé par l'assistant : les pages choisies, remplies avec l'app (prêt à 80 %). */
export function buildDoc(theme: SiteTheme, kinds: PageKind[], f: SiteFacts): SiteDoc {
  const want = new Set<PageKind>([...kinds, 'home', 'legal']);
  const page = (kind: Exclude<PageKind, 'custom'>, blocks: SiteBlock[]): SitePage => {
    const p = PREPARED.find(x => x.kind === kind)!;
    return { id: kind, kind, slug: p.slug, title: p.title, menu: kind !== 'legal', seo: {}, blocks };
  };
  const year = foundingYear(f.tagline);
  const figures = newBlock('figures', { c: { items: [
    ...(year ? [{ n: year, t: 'année de création du club' }] : []),
    { n: String(f.groups.length), t: f.groups.length > 1 ? 'groupes, des jeunes aux seniors' : 'groupe' },
    ...(f.locations.length ? [{ n: String(f.locations.length), t: f.locations.length > 1 ? 'salles d’entraînement' : 'salle d’entraînement' }] : []),
    ...(f.volunteerGroups ? [{ n: String(f.volunteerGroups), t: f.volunteerGroups > 1 ? 'groupes de bénévoles' : 'groupe de bénévoles' }] : []),
  ] } });
  const btns = [{ l: 'Venez essayer', to: 'trial' }, ...(want.has('matches') ? [{ l: 'Voir les matchs', to: 'page:matches' }] : [])];
  const pages: SitePage[] = [];
  pages.push(page('home', [
    newBlock('text', { variant: 'banniere', o: { bg: 'image', sp: 'l', h: 'l' }, c: { k: '', t: f.tagline || `Le basket au ${f.name}`, b: leadText(f), img: null, btns } }),
    newBlock('next'),
    newBlock('homes'),
    newBlock('results', { o: { bg: 'blanc' } }),
    newBlock('trial'),
    newBlock('news'),
    ...(f.agenda ? [newBlock('agenda', { o: { bg: 'blanc' } })] : []),
    ...(f.partners ? [newBlock('partners')] : []),
    figures,
    newBlock('contact'),
  ]));
  if (want.has('club')) pages.push(page('club', [
    newBlock('text', { variant: 'banniere', o: { bg: 'club', sp: 'm', h: 'm' }, c: { k: f.name, t: 'Le club', b: f.tagline ?? '', btns: [] } }),
    newBlock('text', { variant: 'image', o: { bg: 'blanc', sp: 'l' }, c: { k: 'Notre histoire', t: 'Le club depuis ses débuts', b: 'Racontez ici l’histoire du club : sa création, ses grands moments, ses valeurs. (À écrire.)', img: null, btns: [] } }),
    { ...figures, id: newId('b') },
    newBlock('contact', { variant: 'simple' }),
  ]));
  if (want.has('teams')) pages.push(page('teams', [
    newBlock('text', { variant: 'centre', o: { bg: 'club', sp: 'm' }, c: { k: 'Équipes', t: 'Nos équipes', b: 'Une page par groupe : ses entraînements, ses matchs, ses résultats.', btns: [] } }),
    newBlock('teams'),
    newBlock('follow'),
  ]));
  if (want.has('matches')) pages.push(page('matches', [
    newBlock('homes', { o: { n: 0 } }),
    newBlock('away', { o: { bg: 'blanc' } }),
    newBlock('results', { o: { n: 12 } }),
    newBlock('follow'),
  ]));
  if (want.has('partners')) pages.push(page('partners', [
    newBlock('text', { variant: 'centre', o: { bg: 'blanc', sp: 'm' }, c: { k: 'Partenaires', t: 'Ils font vivre le club', b: 'Entreprises, commerces et collectivités : merci à eux. Envie de nous rejoindre ? Écrivez au club.', btns: [] } }),
    newBlock('partners'),
  ]));
  if (want.has('shop')) pages.push(page('shop', [newBlock('shop')]));
  if (want.has('contact')) pages.push(page('contact', [newBlock('contact'), newBlock('faq')]));
  pages.push(page('legal', []));
  return { v: 1, theme, pages, teams: { order: f.groups.map(g => g.id), hidden: [], seo: {} } };
}

// --- Les pages du site, le menu ------------------------------------------------------------------------------------
export interface GroupRef { id: string; name: string; category?: string | null }
/** L'adresse d'une page d'équipe : « equipes/seniors-filles ». */
export const groupSlug = (name: string) => `equipes/${slugify(name) || 'groupe'}`;
/** Les pages d'équipe générées, dans l'ordre choisi (les groupes nouveaux à la fin), sans celles qu'on cache. */
export function teamPages(doc: SiteDoc, groups: GroupRef[]): GroupRef[] {
  const order = doc.teams.order;
  const rank = (id: string) => { const i = order.indexOf(id); return i < 0 ? order.length + groups.findIndex(g => g.id === id) : i; };
  return groups.filter(g => !doc.teams.hidden.includes(g.id)).sort((a, b) => rank(a.id) - rank(b.id));
}
/** Le chemin d'une page sous le site, sans barre au début (« », « le-club », « equipes/u13-filles »). */
export const pagePath = (p: Pick<SitePage, 'slug'>) => p.slug;
/** Ce que désigne un chemin du site : une page, une page d'équipe, les actualités, une actualité ; sinon null. */
export type SiteRoute = { kind: 'page'; page: SitePage } | { kind: 'team'; group: GroupRef } | { kind: 'news' } | { kind: 'article'; id: string };
export function resolvePath(doc: SiteDoc, groups: GroupRef[], path: string): SiteRoute | null {
  const p = path.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (p === 'actualites') return { kind: 'news' };
  const art = /^actualites\/([0-9a-f-]{36})$/.exec(p);
  if (art) return { kind: 'article', id: art[1]! };
  if (doc.pages.some(x => x.kind === 'teams') && p.startsWith('equipes/')) {
    const g = teamPages(doc, groups).find(x => groupSlug(x.name) === p);
    return g ? { kind: 'team', group: g } : null;
  }
  const page = doc.pages.find(x => x.slug === p);
  return page ? { kind: 'page', page } : null;
}

// --- Le texte enrichi (Titre et texte, questions) : un petit balisage sûr --------------------------------------------
// **gras**, *italique*, [texte](https://…), ==couleur du club==, paragraphes séparés par une ligne vide. Tout le reste
// est du texte : jamais de HTML enregistré.
const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const escapeHtml = (s: string) => s.replace(/[&<>"']/g, c => ESC[c]!);
export const SAFE_URL = /^https:\/\/[^\s<>"'`]{1,300}$/;
/** Le balisage en HTML (échappé) : `<p>` par paragraphe, `<br>` pour un retour à la ligne. */
export function richHtml(src: string | null | undefined): string {
  const paras = String(src ?? '').replace(/\r/g, '').split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  return paras.map(p => `<p>${inlineHtml(p)}</p>`).join('');
}
export function inlineHtml(src: string): string {
  let s = escapeHtml(src);
  s = s.replace(/\[([^\]\n]{1,120})\]\((https:\/\/[^\s)<>"']{1,300})\)/g, (_, t: string, u: string) => `<a href="${u}" target="_blank" rel="noopener">${t}</a>`);
  s = s.replace(/\*\*([^*\n]{1,400})\*\*/g, '<b>$1</b>');
  s = s.replace(/(^|[^*])\*([^*\n]{1,400})\*(?!\*)/g, '$1<i>$2</i>');
  s = s.replace(/==([^=\n]{1,200})==/g, '<mark class="sw-hl">$1</mark>');
  return s.replace(/\n/g, '<br>');
}
/** Le balisage sans marques (aperçu Google, listes). */
export const plainText = (src: string | null | undefined) => String(src ?? '').replace(/\[([^\]]*)\]\([^)]*\)/g, '$1').replace(/\*\*|==|\*/g, '').replace(/\s+/g, ' ').trim();

// --- Le référencement ----------------------------------------------------------------------------------------------
export const SEO_TITLE_MAX = 60;
export const SEO_DESC_MAX = 160;
const cut = (s: string, n: number) => (s.length <= n ? s : `${s.slice(0, n - 1).replace(/\s+\S*$/, '')}…`);
/** Le titre proposé : « Seniors filles · Basket Club de Rivebelle » ; l'accueil : le nom du club et son accroche. */
export function seoTitle(pageTitle: string, club: string, tagline?: string | null, home = false): string {
  if (home) return cut(tagline ? `${club} · ${tagline}` : club, SEO_TITLE_MAX);
  return cut(`${pageTitle} · ${club}`, SEO_TITLE_MAX);
}
const DAYS = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
/** La description proposée pour une page d'équipe, d'après ses créneaux et son prochain match à domicile. */
export function seoTeamDesc(slots: { weekdays: number[]; time: string; location: string | null }[], next: { versus: string; date: string } | null): string {
  const s = slots[0];
  const train = s ? `Entraînements le ${s.weekdays.map(d => DAYS[d] ?? '').filter(Boolean).join(' et le ')} à ${s.time.replace(':', 'h')}${s.location ? `, ${s.location}` : ''}.` : '';
  const m = next ? ` Prochain match à domicile : ${next.versus}, le ${next.date}.` : '';
  return cut(`${train}${m}`.trim(), SEO_DESC_MAX);
}
/** La description proposée pour une page : son premier texte, sinon l'accroche du club. */
export function seoDesc(page: SitePage, fallback: string): string {
  for (const b of page.blocks) {
    const t = plainText(String(b.c.b ?? ''));
    if (t.length > 20) return cut(t, SEO_DESC_MAX);
  }
  return cut(fallback, SEO_DESC_MAX);
}

// --- L'option « Site personnalisable » ----------------------------------------------------------------------------
/** « 99,33 € » */
export const euros = (cents: number) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;
/** LE prix de l'option, TTC, en centimes (D-142 : 149 € par an) : le seul endroit où il s'écrit. */
export const OPTION_TTC_CENTS = 14900;
/** D-140 : la première saison se paie au prorata des mois qui restent jusqu'au 30 juin. D-142 laisse la question ouverte :
 *  `false` et l'option se paie l'année entière, partout (offre, éditeur, Site du club, aide, visites, console). */
export const PRORATA = true;
/** « 149 € » (les centimes seulement s'il y en a) */
export const OPTION_EUROS = OPTION_TTC_CENTS % 100 ? euros(OPTION_TTC_CENTS) : `${OPTION_TTC_CENTS / 100} €`;
/** « 149 € TTC par an » */
export const OPTION_PRICE = `${OPTION_EUROS} TTC par an`;
/** « 149 € TTC par an, au prorata » (sans prorata : « 149 € TTC par an ») */
export const OPTION_PRICE_PRORATA = PRORATA ? `${OPTION_PRICE}, au prorata` : OPTION_PRICE;
export interface Prorata { season: string; months: number; ttc: number; ht: number; tva: number; from: string; to: string }
/** Ce que coûte l'option mise en ligne le `day` (AAAA-MM-JJ), jusqu'à la fin de la saison FFBB (1er juillet → 30 juin) qui
 *  le contient ; au prorata, le mois de mise en ligne compte : novembre = 8 mois. Sans prorata : la saison entière. */
export function prorata(day: string, on = PRORATA): Prorata {
  const [y, m] = day.split('-').map(Number) as [number, number];
  const start = m >= 7 ? y : y - 1;
  const months = !on ? 12 : m >= 7 ? 19 - m : 7 - m;
  const ttc = Math.round(OPTION_TTC_CENTS * months / 12);
  const ht = Math.round(ttc / 1.2);
  return { season: `${start}-${start + 1}`, months, ttc, ht, tva: ttc - ht, from: day, to: `${start + 1}-06-30` };
}

// --- Les changements d'un brouillon (« Brouillon · 3 changements », l'historique) -----------------------------------
const pageName = (p: SitePage) => (p.kind === 'home' ? 'l’accueil' : `la page ${p.title}`);
const blockName = (b: SiteBlock) => (b.type === 'text' && String(b.c.t ?? '').trim() ? `« ${cut(plainText(String(b.c.t)), 28)} »` : BLOCKS[b.type].label);
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
/** Ce qui a changé entre la version publiée et le brouillon, en mots (« titre de l’accueil », « bloc Partenaires ajouté »). */
export function changeNotes(prev: SiteDoc | null, next: SiteDoc): string[] {
  if (!prev) return ['site créé'];
  const out: string[] = [];
  if (prev.theme !== next.theme) out.push(`thème ${themeName(next.theme)}`);
  const before = new Map(prev.pages.map(p => [p.id, p]));
  const after = new Map(next.pages.map(p => [p.id, p]));
  for (const p of next.pages) if (!before.has(p.id)) out.push(`page ${p.title} ajoutée`);
  for (const p of prev.pages) if (!after.has(p.id)) out.push(`page ${p.title} retirée`);
  const common = (list: SitePage[], other: Map<string, SitePage>) => list.filter(p => other.has(p.id)).map(p => p.id);
  if (!same(common(prev.pages, after), common(next.pages, before))) out.push('menu réordonné');
  for (const p of next.pages) {
    const o = before.get(p.id);
    if (!o) continue;
    if (o.title !== p.title || o.menu !== p.menu) out.push(`${pageName(p)} : titre ou menu`);
    if (o.slug !== p.slug) out.push(`${pageName(p)} : adresse`);
    if (!same(o.seo, p.seo)) out.push(`${pageName(p)} : référencement`);
    const ob = new Map(o.blocks.map(b => [b.id, b]));
    const nb = new Map(p.blocks.map(b => [b.id, b]));
    for (const b of p.blocks) if (!ob.has(b.id)) out.push(`bloc ${BLOCKS[b.type].label} ajouté (${pageName(p)})`);
    for (const b of o.blocks) if (!nb.has(b.id)) out.push(`bloc ${BLOCKS[b.type].label} retiré (${pageName(p)})`);
    const order = (list: SiteBlock[], other: Map<string, SiteBlock>) => list.filter(b => other.has(b.id)).map(b => b.id);
    if (!same(order(o.blocks, nb), order(p.blocks, ob))) out.push(`ordre des blocs (${pageName(p)})`);
    for (const b of p.blocks) {
      const x = ob.get(b.id);
      if (!x) continue;
      if (b.type === 'text' && x.c.t !== b.c.t && same({ ...x.c, t: null }, { ...b.c, t: null }) && same(x.o, b.o)) out.push(`titre de ${pageName(p)}`);
      else if (!same(x.c.img, b.c.img) && same({ ...x.c, img: null }, { ...b.c, img: null })) out.push(`photo de ${blockName(b)}`);
      else if (!same(x, b)) out.push(`bloc ${blockName(b)} (${pageName(p)})`);
    }
  }
  if (!same(prev.teams, next.teams)) out.push('pages d’équipe');
  return [...new Set(out)];
}

// --- La règle du document (la même que la base : _site_doc_check) ---------------------------------------------------
const PAGE_KINDS: PageKind[] = ['home', 'club', 'teams', 'matches', 'partners', 'shop', 'contact', 'legal', 'custom'];
const ALL_TYPES: BlockType[] = [...LIVE_TYPES, ...FIXED_TYPES];
const ID = /^[a-z0-9][a-z0-9-]{0,39}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const LIVE_KEYS = new Set(['k', 't', 'b', 'img']);
const obj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
/** Toutes les chaînes d'une valeur, avec leur clé. */
function strings(x: unknown, key = '', out: [string, string][] = []): [string, string][] {
  if (typeof x === 'string') out.push([key, x]);
  else if (Array.isArray(x)) x.forEach(v => strings(v, key, out));
  else if (obj(x)) for (const [k, v] of Object.entries(x)) strings(v, k, out);
  return out;
}
/** null si le document est valable, sinon la raison (en mots). */
export function docProblem(d: unknown): string | null {
  if (!obj(d) || d.v !== 1) return 'version';
  if (!['tribune', 'parquet', 'gazette'].includes(String(d.theme))) return 'thème';
  if (JSON.stringify(d).length > MAX_DOC) return 'trop long';
  const pages = d.pages;
  if (!Array.isArray(pages) || pages.length < 1 || pages.length > MAX_PAGES) return 'pages';
  const ids = new Set<string>(), slugs = new Set<string>(), blockIds = new Set<string>();
  let homes = 0, legals = 0;
  const kinds = new Map<string, number>();
  for (const p of pages) {
    if (!obj(p) || typeof p.id !== 'string' || !ID.test(p.id) || ids.has(p.id)) return 'page : identifiant';
    ids.add(p.id);
    if (!PAGE_KINDS.includes(p.kind as PageKind)) return 'page : sorte';
    kinds.set(String(p.kind), (kinds.get(String(p.kind)) ?? 0) + 1);
    if (p.kind !== 'custom' && kinds.get(String(p.kind))! > 1) return 'page en double';
    if (typeof p.slug !== 'string' || p.slug.length > 40 || slugs.has(p.slug)) return 'page : adresse';
    if (p.kind === 'home' ? p.slug !== '' : !PAGE_SLUG.test(p.slug) || RESERVED_PAGE_SLUGS.includes(p.slug)) return 'page : adresse';
    slugs.add(p.slug);
    if (typeof p.title !== 'string' || !p.title.trim() || p.title.length > 60) return 'page : titre';
    if (typeof p.menu !== 'boolean') return 'page : menu';
    if (!obj(p.seo)) return 'page : référencement';
    if ((p.seo.title != null && (typeof p.seo.title !== 'string' || p.seo.title.length > 70))
      || (p.seo.desc != null && (typeof p.seo.desc !== 'string' || p.seo.desc.length > 200))) return 'page : référencement';
    if (!Array.isArray(p.blocks) || p.blocks.length > MAX_BLOCKS) return 'page : blocs';
    if (p.kind === 'home') homes++;
    if (p.kind === 'legal') { legals++; if (p.blocks.length) return 'mentions légales : générées'; }
    for (const b of p.blocks) {
      if (!obj(b) || typeof b.id !== 'string' || !ID.test(b.id) || blockIds.has(b.id)) return 'bloc : identifiant';
      blockIds.add(b.id);
      if (!ALL_TYPES.includes(b.type as BlockType)) return 'bloc : sorte';
      if (typeof b.variant !== 'string' || !/^[a-z-]{1,20}$/.test(b.variant)) return 'bloc : variante';
      if (!obj(b.o) || !obj(b.c)) return 'bloc : contenu';
      if (isLive(b.type as BlockType) && Object.keys(b.c).some(k => !LIVE_KEYS.has(k))) return 'bloc vivant : contenu';
    }
  }
  if (homes !== 1 || legals !== 1) return 'accueil et mentions légales';
  if (!obj(d.teams) || !Array.isArray(d.teams.order) || !Array.isArray(d.teams.hidden) || !obj(d.teams.seo)) return 'pages d’équipe';
  if ([...d.teams.order, ...d.teams.hidden].some(x => typeof x !== 'string' || !UUID.test(x)) || d.teams.order.length > 80) return 'pages d’équipe';
  for (const [k, s] of strings(d)) {
    if (s.length > 6000) return 'texte trop long';
    if (/<\s*\/?\s*script|javascript:/i.test(s)) return 'texte interdit';
    if (s.startsWith('url:') && !SAFE_URL.test(s.slice(4))) return 'lien : https seulement';
    if (k === 'yt' && s && !/^[A-Za-z0-9_-]{11}$/.test(s)) return 'vidéo YouTube';
  }
  return null;
}
/** Les photos et documents du club cités par un document (pour la lecture publique et la suppression). */
export function docMedia(d: SiteDoc): string[] {
  const out = new Set<string>();
  for (const p of d.pages) for (const b of p.blocks) for (const [, s] of strings(b.c)) if (UUID.test(s)) out.add(s);
  return [...out];
}

// --- Un lien de bouton ---------------------------------------------------------------------------------------------
/** Où mène un bouton : « trial » (demande d'essai), « follow », « page:<id> », « url:https://… ». */
export type LinkTo = string;
export function linkLabel(to: LinkTo, doc: SiteDoc): string {
  if (to === 'trial') return 'demande d’essai';
  if (to === 'follow') return '☆ Suivre';
  if (to.startsWith('page:')) { const p = doc.pages.find(x => x.id === to.slice(5)); return p ? `page ${p.title}` : 'page'; }
  if (to.startsWith('url:')) return to.slice(4).replace(/^https:\/\//, '').slice(0, 32);
  return '—';
}

// --- La boutique (V1 : des liens de paiement saisis dans l'app ; Arceau n'encaisse rien) ----------------------------
export type ShopKind = 'helloasso' | 'cotisation' | 'don' | 'autre';
export interface ShopLink { k: ShopKind; l: string; d?: string | null; u: string; p?: string | null }
export const SHOP_KINDS: { key: ShopKind; label: string }[] = [
  { key: 'cotisation', label: 'Cotisation' }, { key: 'helloasso', label: 'HelloAsso' }, { key: 'don', label: 'Don' }, { key: 'autre', label: 'Autre' },
];
export function shopProblem(list: unknown): string | null {
  if (!Array.isArray(list) || list.length > 12) return 'au plus 12 liens';
  for (const x of list) {
    if (!obj(x) || !SHOP_KINDS.some(k => k.key === x.k)) return 'sorte';
    if (typeof x.l !== 'string' || !x.l.trim() || x.l.length > 60) return 'nom';
    if (x.d != null && (typeof x.d !== 'string' || x.d.length > 140)) return 'description';
    if (x.p != null && (typeof x.p !== 'string' || x.p.length > 30)) return 'prix';
    if (typeof x.u !== 'string' || !SAFE_URL.test(x.u)) return 'lien https';
  }
  return null;
}

/** Une vidéo YouTube : l'identifiant d'un lien collé (watch?v=, youtu.be/, shorts/, embed/), sinon null. */
export function youtubeId(input: string): string | null {
  const s = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const m = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/.exec(s);
  return m ? m[1]! : null;
}
